// linkedDelta — pure zone-transform engine for the linked multi-mockup placement
// editor (Model B). A single gesture on ONE mockup produces an abstract transform;
// that SAME transform is applied to the matching zone of every OTHER mockup, each
// from its OWN baseline — so each design moves the same distance / scales by the
// same factor / rotates by the same amount, staying in the same place relative to
// its own shirt.
//
// Zones are percent-of-image {id, group, x, y, w, h, rotation?} rectangles, or
// perspective quads {id, group, corners:{tl,tr,br,bl}} (see App.jsx migrate()).
// Everything here is percent-space; no pixels, no DOM. Rotation is degrees,
// clamped to the editor's existing -45..45 range.
//
// Transform descriptors (computed by the editor from pointer/slider gestures):
//   {type:'move',   dx, dy}    — additive percent shift
//   {type:'scale',  factor}    — multiplicative size change about the zone centre
//   {type:'rotate', delta}     — additive degrees
//
// Skip-and-report: a target with no matching-group zone is skipped ('no-zone');
// rotation has no clean meaning on a free perspective quad (and the model zeroes
// rotation when corners exist), so it's skipped ('perspective-rotate'). Nothing is
// ever mangled — a skipped zone is returned untouched and reported to the member.

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const r2 = (v) => Math.round(v * 100) / 100;
const MIN_SIZE = 3; // percent — matches the editor's corner-resize floor
// Move is SOFT-bounded, not clamped on-image: a zone may carry well past the
// frame (the bake simply crops at the image edge — proven safe, the single-zone
// editor already emits off-frame zones), but at least this fraction of the zone
// stays on-image on EACH axis so it can never be dragged entirely off and lost
// (a fully off-frame zone bakes empty — a silent failure). Size floor (MIN_SIZE)
// and the -45..45 rotation range are unchanged; only position is softened.
const KEEP_FRAC = 0.25;

// The matching zone for a group on a mockup = the first zone assigned to it.
// (Mockups carry one zone per group in the common case; front+sleeve sets have
// one zone per group and the linked edit targets a single group at a time.)
export function matchZone(mockup, groupId) {
  const sos = (mockup && mockup.smartObjects) || [];
  return sos.find((s) => s && s.group === groupId) || null;
}

function cornersBBox(cn) {
  const xs = [cn.tl.x, cn.tr.x, cn.br.x, cn.bl.x];
  const ys = [cn.tl.y, cn.tr.y, cn.br.y, cn.bl.y];
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
}

function mapCorners(cn, fn) {
  return { tl: fn(cn.tl), tr: fn(cn.tr), br: fn(cn.br), bl: fn(cn.bl) };
}

function roundCorners(cn) {
  return mapCorners(cn, (p) => ({ x: r2(p.x), y: r2(p.y) }));
}

function moveRect(zone, dx, dy) {
  const w = zone.w, h = zone.h;
  // Keep at least KEEP_FRAC of each dimension on-image; everything else is free.
  const keepX = w * KEEP_FRAC, keepY = h * KEEP_FRAC;
  const x = clamp(zone.x + dx, keepX - w, 100 - keepX);
  const y = clamp(zone.y + dy, keepY - h, 100 - keepY);
  return { ...zone, x: r2(x), y: r2(y) };
}

function moveCorners(zone, dx, dy) {
  const bb = cornersBBox(zone.corners);
  // Soft-bound the translation so at least KEEP_FRAC of the quad's bounding box
  // stays on-image on each axis (same rule as moveRect, applied to the bbox).
  const keepX = (bb.maxX - bb.minX) * KEEP_FRAC, keepY = (bb.maxY - bb.minY) * KEEP_FRAC;
  const cdx = clamp(dx, keepX - bb.maxX, 100 - keepX - bb.minX);
  const cdy = clamp(dy, keepY - bb.maxY, 100 - keepY - bb.minY);
  const corners = roundCorners(mapCorners(zone.corners, (p) => ({ x: p.x + cdx, y: p.y + cdy })));
  return { ...zone, corners };
}

function scaleRect(zone, factor) {
  const cx = zone.x + zone.w / 2, cy = zone.y + zone.h / 2;
  const w = clamp(zone.w * factor, MIN_SIZE, 100);
  const h = clamp(zone.h * factor, MIN_SIZE, 100);
  const x = clamp(cx - w / 2, 0, Math.max(0, 100 - w));
  const y = clamp(cy - h / 2, 0, Math.max(0, 100 - h));
  return { ...zone, w: r2(w), h: r2(h), x: r2(x), y: r2(y) };
}

function scaleCorners(zone, factor) {
  const bb = cornersBBox(zone.corners);
  const cx = (bb.minX + bb.maxX) / 2, cy = (bb.minY + bb.maxY) / 2;
  let cn = mapCorners(zone.corners, (p) => ({ x: cx + (p.x - cx) * factor, y: cy + (p.y - cy) * factor }));
  // Shift the scaled quad back on-image if it spilled past an edge (preserves
  // shape rather than capping size — perspective zones are rare edge cases).
  const nb = cornersBBox(cn);
  let sx = 0, sy = 0;
  if (nb.minX < 0) sx = -nb.minX; else if (nb.maxX > 100) sx = 100 - nb.maxX;
  if (nb.minY < 0) sy = -nb.minY; else if (nb.maxY > 100) sy = 100 - nb.maxY;
  if (sx || sy) cn = mapCorners(cn, (p) => ({ x: p.x + sx, y: p.y + sy }));
  return { ...zone, corners: roundCorners(cn) };
}

// Apply one abstract transform to one zone's baseline. Returns {zone} with the new
// zone, or {skip:<reason>} with the reason the transform could not be applied.
export function applyTransform(zone, tf) {
  if (!zone) return { skip: "no-zone" };
  if (tf.type === "rotate") {
    if (zone.corners) return { skip: "perspective-rotate" };
    const rotation = clamp(Math.round((zone.rotation || 0) + tf.delta), -45, 45);
    return { zone: { ...zone, rotation } };
  }
  if (tf.type === "move") {
    return { zone: zone.corners ? moveCorners(zone, tf.dx, tf.dy) : moveRect(zone, tf.dx, tf.dy) };
  }
  if (tf.type === "scale") {
    return { zone: zone.corners ? scaleCorners(zone, tf.factor) : scaleRect(zone, tf.factor) };
  }
  return { zone };
}

// Human-readable reason strings for the skip-and-report strip.
export const SKIP_REASONS = {
  "no-zone": "no zone for this group",
  "perspective-rotate": "perspective zone can't be rotated",
};
